import axios from "axios";
import React, {
  createContext,
  useState,
  useContext,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";

const FilterContext = createContext();

export const useFilters = () => useContext(FilterContext);

export const FilterProvider = ({ children }) => {
  const [filters, setFilters] = useState({});
  const [pendingFilters, setPendingFilters] = useState({}); // For mobile apply button
  const [openMainCategories, setOpenMainCategories] = useState({});
  const [openSubcategories, setOpenSubcategories] = useState({});
  const [loader, setLoader] = useState(false);
  const [isSkeletonLoading, setIsSkeletonLoading] = useState(true);
  // const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isPendingActive, setIsPendingActive] = useState(false);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [mobilePopUpData, setMobilePopUpData] = useState();
  const [openPopup, setopenPopup] = useState();
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;

  // AbortController ref for cancelling ongoing requests
  const abortControllerRef = useRef(null);
  // Debounce timer ref
  const debounceTimerRef = useRef(null);
  // Skip one fetch when we auto-apply filters from server response
  const skipNextFetchRef = useRef(false);
  // Track auto-selected options suppressed by user actions
  const suppressedAutoSelectedRef = useRef(new Set());
  const suppressAllAutoSelectRef = useRef(false);
  // Track if filters were just cleared to prevent immediate auto-reselection
  const justClearedRef = useRef(false);
  // Track if pagination is being reset to prevent race conditions
  const isResettingPaginationRef = useRef(false);

  const toKey = (s) =>
    String(s ?? "")
      .trim()
      .toLowerCase();
  const getOptionKey = (opt) => toKey(opt?.label ?? opt?.value);
  const buildSectionKey = (title, label) =>
    `${title}-${label}`.toLowerCase().replace(/\s+/g, "_");

  // Helper function to check if a filter is an "All" filter (starts with "All" and ends with "Baskets" or "Items")
  const isAllFilter = (label) => {
    const key = toKey(label);
    return /^all\s+.*\s+(baskets|items)$/.test(key);
  };

  // Helper function to get all current "All" filters
  const getAllFilters = (filterObj) => {
    const allFilters = [];
    Object.entries(filterObj).forEach(([category, options]) => {
      options.forEach((opt) => {
        const label = opt.label ?? opt.value;
        if (isAllFilter(label)) {
          allFilters.push({ category, option: opt, label });
        }
      });
    });
    return allFilters;
  };

  // const mappingRootRedirection = {
  //   "2326": "baby",
  //   "2328": "hh",
  //   "2330": "pet",
  //   "2329": "kids",
  // };

  const [mappingRootRedirection, setmappingRootRedirection] = useState({})

  const fetchCategories = async () => {
    try {
      const res = await axios.post(`${APIURL}api/BasketOption/`);
      const data = res.data.data || null

      if (data) {
        // Map API keys to your desired short keys
        const keyMapping = {
          babyCategoryId: "baby",
          himHerCategoryId: "hh",
          petsCategoryId: "pet",
          kidsCategoryId: "kids"
        };

        // Dynamically generate the mappingRootRedirection object
        const mappingRootRedirection = Object.fromEntries(
          Object.entries(keyMapping).map(([apiKey, shortKey]) => [data[apiKey], shortKey])
        );

        setmappingRootRedirection(mappingRootRedirection || {})
      } else {
        setmappingRootRedirection({})
      }
    } catch (err) {
      console.error('Error in fetched category', err);
    }
  }

  useEffect(() => {
    const initFetchCategories = async () => {
      await fetchCategories()
    }

    initFetchCategories()
  }, [])

  const getRedirectionURL = (segment) => {
    switch (segment) {
      case "baby":
        return {
          "shop-by-item": "/baby-gifts/shop-by-item/by-size/shop-all-baby-items/",
          "baby-gift-baskets":
            "/baby-gifts/baby-gift-baskets/shop-all-baby-gift-baskets/",
        };
      case "hh":
        return {
          "shop-by-item": "/him-her-gifts/gift-baskets/by-occasion/shop-all-him-her-items/",
          "gift-baskets-for-him-&-her":
            "/him-her-gifts/gift-baskets/by-type/shop-all-gift-baskets/",
        };
      case "pet":
        return {
          "shop-by-item": "/pet-gifts/shop-by-item/shop-all-pet-items/",
          "dog-gift-baskets":
            "/pet-gifts/dog-gift-baskets/by-gender/shop-all/",
        };
      case "kids":
        return {
          "shop-by-item": "/kids-gifts/shop-by-item/by-age/shop-all-kids-items/",
          "kid's-gift-baskets":
            "/kids-gifts/kids-gift-baskets/by-gender/boy/shop-all-kids-baskets/",
        };
      default:
        break;
    }
  };

  // Function to handle body overflow when loader is active
  const handleBodyOverflow = (shouldHide) => {
    if (typeof document !== "undefined") {
      if (shouldHide) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "";
      }
    }
  };

  // Ensure each option has a stable label key for selection (prefer label, fallback to value)
  const normalizeOption = (option) => ({
    ...option,
    label: option?.label ?? option?.value,
  });

  // Seed pending filters from current applied filters (used when opening mobile panel)
  const seedPendingFromFilters = useCallback(() => {
    const seeded = Object.fromEntries(
      Object.entries(filters).map(([cat, opts]) => [
        cat,
        (opts || []).map(normalizeOption),
      ])
    );
    setPendingFilters(seeded);
  }, [filters]);

  const beginPendingSession = useCallback(() => {
    setIsPendingActive(true);
  }, []);

  const endPendingSession = useCallback(() => {
    setIsPendingActive(false);
  }, []);

  // Handle filter change - store in pending filters during pending session, apply immediately otherwise
  const handleFilterChange = (category, rawOption) => {
    setTimeout(() => {
      // scroll to top
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      })
    }, 1500);

    const option = normalizeOption(rawOption);
    const optionKey = getOptionKey(option);
    const optionLabel = option.label ?? option.value;
    const isCurrentOptionAll = isAllFilter(optionLabel);

    // Any manual interaction should re-enable category-level auto-select
    suppressAllAutoSelectRef.current = false;
    // localStorage.setItem("pathname", null);  commented code pathname
    if (isPendingActive) {
      // In pending session: store in pending filters
      setPendingFilters((prev) => {
        const existing = prev[category] || [];
        const alreadySelected = existing.some(
          (item) => getOptionKey(item) === optionKey
        );

        let updated;

        if (alreadySelected) {
          // User is deselecting the option
          updated = existing.filter((item) => getOptionKey(item) !== optionKey);
        } else {
          // User is selecting the option
          if (isCurrentOptionAll) {
            // If selecting an "All" filter, clear all other filters
            updated = [option];

            const parentCategoryID = document.getElementById("parent_level_cat")?.value || null;
            const categoryID = document.getElementById("category-id")?.value || null;

            const saperatedCategoryName = category.split('-')
            const joinedTitle = saperatedCategoryName[0]
              .toLowerCase()
              .replace(/_/g, "-")      // convert underscores to hyphens
              .replace(/\s+/g, "-");   // (optional) convert spaces if any

            const redirectionURL = getRedirectionURL(mappingRootRedirection[parentCategoryID || categoryID]);

            window.location.href = redirectionURL[joinedTitle];
          } else {
            // No "All" filters, just add the new filter
            updated = [...existing, option];

            // If selecting a non-"All" filter, check if any "All" filters exist
            // const existingAllFilters = existing.filter((item) => isAllFilter(item.label ?? item.value));

            // if (existingAllFilters.length > 0) {
            //   // Remove all "All" filters and add the new filter
            //   updated = [...existing.filter((item) => !isAllFilter(item.label ?? item.value)), option];
            // } else {
            //   // No "All" filters, just add the new filter
            //   updated = [...existing, option];
            // }
          }
        }

        // If selecting an "All" filter, clear filters in all other categories
        if (isCurrentOptionAll && !alreadySelected) {
          const newPendingFilters = { [category]: updated };
          return newPendingFilters;
        }

        return { ...prev, [category]: updated };
      });
    } else {
      // Set loader immediately for better UX
      setLoader(true);
      // Apply immediately
      applyFilters(category, option);
    }
  };

  const flattenObj = (obj) => {
    return Object.values(obj).flat();
  };

  const removeAllFiltersFromObject = (obj) => {
    const newObj = {};

    Object.entries(obj).forEach(([key, arr]) => {

      const filteredArr = arr.filter(
        (item) => !isAllFilter(item.label ?? item.value)
      );

      // Only keep the key if items remain
      if (filteredArr.length > 0) {
        newObj[key] = filteredArr;
      }
    });

    return newObj;
  };

  // Apply filters function - used for immediate application
  const applyFilters = (category, rawOption) => {
    const option = normalizeOption(rawOption);
    const optionKey = getOptionKey(option);
    const optionLabel = option.label ?? option.value;
    const isCurrentOptionAll = isAllFilter(optionLabel);

    setFilters((prev) => {
      const existing = prev[category] || [];

      const existingFlattened = flattenObj(prev);

      const alreadySelected = existing.some(
        (item) => getOptionKey(item) === optionKey
      );

      let updated;

      if (alreadySelected) {
        // User is deselecting the option
        updated = existing.filter((item) => getOptionKey(item) !== optionKey);
      } else {
        // User is selecting the option
        if (isCurrentOptionAll) {
          // If selecting an "All" filter, clear all other filters
          updated = [option];

          const parentCategoryID = document.getElementById("parent_level_cat")?.value || null;
          const categoryID = document.getElementById("category-id")?.value || null;

          const saperatedCategoryName = category.split('-')

          const joinedTitle = saperatedCategoryName[0]
            .toLowerCase()
            .replace(/_/g, "-")      // convert underscores to hyphens
            .replace(/\s+/g, "-");   // (optional) convert spaces if any

          const redirectionURL = getRedirectionURL(mappingRootRedirection[parentCategoryID || categoryID]);
          window.location.href = redirectionURL[joinedTitle];
        } else {
          // No "All" filters, just add the new filter
          updated = [...existing, option];

          // If selecting a non-"All" filter, check if any "All" filters exist
          // const existingAllFilters = existingFlattened.filter((item) => isAllFilter(item.label ?? item.value));

          // if (existingAllFilters.length > 0) {
          //   // Remove all "All" filters and add the new filter

          //   console.log('Removed All filter and adding new option', existingFlattened)
          //   updated = [...existing.filter((item) => !isAllFilter(item.label ?? item.value)), option];
          // } else {
          //   // No "All" filters, just add the new filter
          //   updated = [...existing, option];
          // }
        }
      }

      // If user deselects an auto-selected option, remember to suppress it
      if (alreadySelected) {
        suppressedAutoSelectedRef.current.add(`${category}::${optionKey}`);
      } else {
        // User explicitly added it back; remove from suppression
        suppressedAutoSelectedRef.current.delete(`${category}::${optionKey}`);
      }

      // If selecting an "All" filter, clear filters in all other categories
      if (isCurrentOptionAll && !alreadySelected) {
        const newFilters = { [category]: updated };
        return newFilters;
      }

      return { ...prev, [category]: updated };
    });

    // Trigger API call with debounce
    debouncedTriggerApiCall();
  };

  // Apply pending filters (mobile apply button)
  const applyPendingFilters = () => {
    // Normalize any pending options to guarantee label presence
    const normalized = Object.fromEntries(
      Object.entries(pendingFilters).map(([cat, opts]) => [
        cat,
        (opts || []).map(normalizeOption),
      ])
    );
    setFilters(normalized);
    setPendingFilters({});
    // Trigger API call immediately (no debounce for apply button)
    triggerApiCall();
  };

  // Clear pending filters (mobile cancel)
  const clearPendingFilters = () => {
    setPendingFilters({});
  };

  // Remove single pending filter by label (used inside mobile drawer)
  const removePendingFilter = (category, optionLabel) => {
    const targetKey = toKey(optionLabel);
    setPendingFilters((prev) => {
      const current = prev[category] || [];
      const updatedCategory = current.filter(
        (item) => getOptionKey(item) !== targetKey
      );
      const next = { ...prev, [category]: updatedCategory };
      if (updatedCategory.length === 0) {
        delete next[category];
      }
      return next;
    });
    // Also suppress auto-select for this removed option
    suppressedAutoSelectedRef.current.add(`${category}::${targetKey}`);
  };

  const toggleMainCategory = (title) => {
    setOpenMainCategories((prev) => ({ ...prev, [title]: !prev[title] }));
  };

  const toggleSubcategory = (key) => {
    setOpenSubcategories((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const removeFilter = (category, optionLabel) => {
    const targetKey = toKey(optionLabel);
    setFilters((prev) => {
      const updatedCategory = (prev[category] || []).filter(
        (item) => getOptionKey(item) !== targetKey
      );
      const updatedFilters = { ...prev, [category]: updatedCategory };
      // localStorage.setItem("pathname", null); // commented code pathname
      // Remove category if no options left
      if (updatedCategory.length === 0) {
        delete updatedFilters[category];
      }

      return updatedFilters;
    });

    // Suppress auto-select from re-adding this option
    suppressedAutoSelectedRef.current.add(`${category}::${targetKey}`);

    // Set loader immediately for better UX
    setLoader(true);
    // Trigger API call with debounce
    debouncedTriggerApiCall();
  };

  const clearAllFilters = async () => {

    const hasFourthLevel = filterData.some(item => item.isfourthlevel === true);

    // console.log(hasFourthLevel,'hasFourthLevel...........')

    if (hasFourthLevel == true) {
      const parentCategoryID = document.getElementById("parent_level_cat")?.value || null;
      const categoryID = document.getElementById("category-id")?.value || null;

      filterData.forEach((main, idx) => {
        if (main.open == true) {
          const joinedTitle = main.title.toLowerCase().replace(/\s+/g, "-")
          if (Object.keys(mappingRootRedirection).length > 0) {
            const redirectionURL = getRedirectionURL(
              mappingRootRedirection[parentCategoryID || categoryID]
            );

            window.location.href = redirectionURL[joinedTitle];

            return
          }
        }
      });
    }

    // const currentPath = localStorage.getItem("pathname");

    setFilters({});
    setPendingFilters({});

    // Redirect to the appropriate URL based on the current path (4th level category)
    // const pathSegments = currentPath.split("/").filter(Boolean);
    // const firstLevelSegment = pathSegments[0]
    // const secondLevelSegment = pathSegments[1]

    // if (pathSegments.length == 4) {
    //   const redirectionURL = getRedirectionURL(mappingRootRedirection[firstLevelSegment])
    //   if (redirectionURL) {
    //     window.location.href = redirectionURL[secondLevelSegment]
    //   }
    // }

    // Suppress all future auto-selects until the user interacts again
    suppressAllAutoSelectRef.current = true;
    suppressedAutoSelectedRef.current = new Set();
    // Mark that filters were just cleared to prevent immediate auto-reselection
    justClearedRef.current = true;
    // Set loader immediately for better UX
    setLoader(true);
    // Trigger API call immediately (no debounce for clear all)
    setPage(1);
    // localStorage.setItem("pathname", null); // commented code pathname
    triggerApiCall();
  };

  // Memoize selectedFilters
  const selectedFilters = useMemo(() => {
    return Object.entries(filters).flatMap(([category, options]) =>
      options.map((opt) => ({
        category,
        label: opt.label ?? opt.value,
        value: opt.value,
        id: opt.id, // Keep ID for API calls
      }))
    );
  }, [filters]);

  // Memoize pendingFilters for display (not shown in UI but used for logic)
  const pendingFiltersList = useMemo(() => {
    return Object.entries(pendingFilters).flatMap(([category, options]) =>
      (options || []).map((opt) => ({
        category,
        label: opt.label ?? opt.value,
        value: opt.value,
        id: opt.id, // Keep ID for API calls
      }))
    );
  }, [pendingFilters]);

  const APIURL = "https://admin.ourgreenhouse.com/" // Live
  // const APIURL = "https://ourgreenhouse-staging-0d60dd13106b.herokuapp.com/" // Staging
  // const APIURL = "http://localhost:3000/"

  const [sortOption, setSortOption] = useState(undefined);
  const [sortReady, setSortReady] = useState(false);

  function handleSortChange(event) {
    setSortOption(event.target.value);
    setPage(1);
    // Set loader immediately for better UX
    setLoader(true);
    // Trigger API call immediately (no debounce for sort)
    triggerApiCall();
  }

  const [productData, setProductData] = useState([]);
  const [productCount, setproductCount] = useState([]);

  // --- ADD REDIRECT LOGIC HERE ---
  let fullPath = window.location.pathname; // commented code pathname

  // Extract top-level category from pathname
  const pathSegments = fullPath.split("/").filter(Boolean); // removes empty strings  // commented code pathname

  // console.log(pathSegments,'pathSegments......')
  let topLevelCategory = pathSegments[0] || ""; // fallback if missing //commented code pathname 
  // Check existing localStorage
  // const savedPath = localStorage.getItem("pathname");  // commented code pathname
  let redirectPath = `/${topLevelCategory}/`;  // commented code pathname
  localStorage.setItem("pathname", fullPath);
  // Only replace localStorage if path has more than one segment

  // if (pathSegments.length == 1) {
  //   const wasRedirected = localStorage.getItem("isRedirected");

  //   if (wasRedirected == 'true') {
  //     const path = localStorage.getItem("pathname");
  //     localStorage.setItem("pathname", path);
  //   } else {
  //     localStorage.setItem('isRedirected', 'false')
  //     localStorage.setItem("pathname", fullPath);
  //   }
  // } else if (pathSegments.length > 1) {
  //   // Update localStorage with full path
  //   localStorage.setItem("pathname", fullPath);
  //   localStorage.setItem("isRedirected", 'true');

  //   // Update URL to top-level only
  //   window.history.replaceState(null, "", redirectPath);
  // } else if (!savedPath) {
  //   // If localStorage is empty, save the single segment path
  //   localStorage.setItem("pathname", fullPath);
  // }

  // commented code pathname
  // if (pathSegments.length > 1) {
  //   // Update localStorage with full path
  //   localStorage.setItem("pathname", fullPath);

  //   // Update URL to top-level only
  //   // window.history.replaceState(null, "", redirectPath);
  // } else if (!savedPath) {
  //   // If localStorage is empty, save the single segment path
  //   localStorage.setItem("pathname", fullPath);
  // }
  // // else if (redirectPath !== savedPath) {
  // //   localStorage.setItem("pathname", fullPath);
  // // }

  // // Use saved path for API calls
  // var pathLink = localStorage.getItem("pathname") || fullPath;

  // // Redirect if path has more than top-level
  // if (pathSegments.length > 1) {
  //   // window.location.href = `${window.location.protocol}//${window.location.host}${redirectPath}`;
  // }

  // Store full original path for API usage
  // let pathLink = fullPath;
  const categoryId = document.getElementById("category-id")?.value;
  const categoryName = document.getElementById("category-name")?.value || "";
  const categoryDescription =
    document.getElementById("category-description")?.value || "";
  const limit = 20;
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [filterData, setFilterData] = useState([]);
  const mainCategoryId = document.getElementById("parent_level_cat")?.value;
  // const mainCategoryId = document.getElementById("parent_level_cat")?.value || 2326;
  // let pathLink = window.location.pathname !== "/"
  // ? window.location.pathname
  // : "/baby-gifts/shop-by-item/our-collections/organic-baby-knits/";
  // let pathLink = window.location.pathname

  const observerRef = useRef(null);

  // Function to trigger API call with proper cancellation
  const triggerApiCall = useCallback(() => {
    // Cancel any ongoing request
    // if (abortControllerRef.current) {
    //   abortControllerRef.current.abort();
    // }

    // Create new AbortController for this request
    // abortControllerRef.current = new AbortController();

    // Set flag to prevent IntersectionObserver from triggering during reset
    isResettingPaginationRef.current = true;
    
    // Reset products and pagination for new filter/sort
    setProductData([]);
    setPage(1);
    setHasMore(true);
    setLoader(true);
    
    // Clear the flag after state updates and first page load completes
    setTimeout(() => {
      isResettingPaginationRef.current = false;
    }, 500);
  }, []);

  // Debounced API trigger to prevent excessive calls
  const debouncedTriggerApiCall = useCallback(() => {
    // Clear existing timer
    // if (debounceTimerRef.current) {
    //   clearTimeout(debounceTimerRef.current);
    // }

    // Set new timer
    // debounceTimerRef.current = setTimeout(() => {
    triggerApiCall();
    // }, 300); // 300ms debounce
  }, [triggerApiCall]);

  const fetchDefaultsorting = async () => {
    const payload = {
      cat_id: categoryId
    }

    try {
      const res = await axios.post(`${APIURL}api/filter_v1/fetchSingleCategory/`, payload);
      if (res && Object.keys(res.data.data).length > 0) {
        const bc_category_default_sort = res.data.data.category_data.default_sorting || null;
        // console.log(fetchDefaultSorting(bc_category_default_sort), 'res.........')
        return fetchDefaultSorting(bc_category_default_sort) || null
      } else {
        return null
      }
    } catch (err) {
      console.error('Error in fetched category', err);
    }
  }

  useEffect(() => {
    const selectedSortBy = async () => {
      const defaultSort = await fetchDefaultsorting()
      setSortOption(defaultSort || "5")
      setSortReady(true)
    }
    selectedSortBy()
  }, [])

  const fetchProducts = useCallback(async () => {
    if (loading || !hasMore) return;
    if (isPendingActive) return; // pause loading while filter drawer is open
    if (!sortReady) return; // wait until sort option is loaded

    setLoading(true);

    const params = new URLSearchParams({
      category_id: categoryId,
      page,
      limit,
      parent_id: mainCategoryId
      // path: '/baby-gifts/'
      // path: '/baby-gifts/shop-by-item/'
      // path: '/baby-gifts/shop-by-item/our-collections/'
      // path: '/baby-gifts/shop-by-item/our-collections/organic-baby-knits/'
      // path: pathLink,
    });

    // const defaultSort = await fetchDefaultsorting()

    // // if (sortOption && sortOption !== "0") {
   
    console.log(sortOption,'sortOption...........')
    if (sortOption !== null && sortOption !== undefined && sortOption !== "") {
      params.append("sort", sortOption);
    }

    //   if (defaultSort !== null && defaultSort !== undefined) {
    //     params.append("sort", defaultSort);
    //   }
    // }

    console.log(filters,selectedFilters,'selectedFilters...............')

    selectedFilters.forEach((f) => {
      params.append(`custom_field[${f.id}]`, f.value);
    });

    try {
      // const paramssss = Object.fromEntries(new URLSearchParams(params));

      // console.log(paramssss, 'params..........');
      // old
      // const res = await axios.get(`${APIURL}api/filter/?${params.toString()}`, {
      //   signal: abortControllerRef.current?.signal,
      // });

      const res = await axios.get(`${APIURL}api/filter_v1/?${params.toString()}`, {
        signal: abortControllerRef.current?.signal,
      });

      const newProducts = res.data.data.products || [];
      const newFilters = res.data.data.filters;
      const productCount = res.data.data.pagination.total_products;
      setproductCount(productCount);

      // Merge filter data and handle auto-open/auto-select
      setFilterData((prevFilters) => {
        if (!prevFilters || prevFilters.length === 0) return newFilters;

        // Create a map of previous filters for easy lookup
        const prevMap = new Map(prevFilters.map((cat) => [cat.title, cat]));

        // Merge only categories present in newFilters
        const mergedFilters = newFilters.map((newCat) => {
          const prevCat = prevMap.get(newCat.title);
          if (!prevCat) return newCat; // completely new category

          // Merge sections
          const mergedSections = newCat.sections.map((newSec) => {
            const prevSec = prevCat.sections.find(
              (sec) => sec.label === newSec.label
            );
            if (!prevSec) return newSec; // new section

            // Merge options, prefer new options if present
            const mergedOptions =
              newSec.options.length > 0 ? newSec.options : prevSec.options;

            return {
              ...prevSec,
              options: mergedOptions,
              has_options: mergedOptions.length > 0,
            };
          });

          return {
            ...prevCat,
            sections: mergedSections,
          };
        });

        return mergedFilters;
      });

      // Detect if device is mobile (<=768px, adjust if needed)

      // Handle auto-open categories/sections based on "open": true
      const autoOpenCategories = {};
      const autoOpenSections = {};

      newFilters.forEach((category) => {
        let categoryShouldOpen = false;

        category.sections.forEach((section) => {
          const hasSelectedOption = Array.isArray(section.options)
            ? section.options.some((opt) => opt?.selected === true)
            : false;

          if (isMobile) {
            // ✅ Mobile: only open if a selected option exists
            if (hasSelectedOption) {
              const sectionKey = buildSectionKey(category.title, section.label);
              autoOpenSections[sectionKey] = true;
              categoryShouldOpen = true;
            }
          } else {
            // ✅ Desktop: respect open:true OR selected option
            if (section.open === true || hasSelectedOption) {
              const sectionKey = buildSectionKey(category.title, section.label);
              autoOpenSections[sectionKey] = true;
              categoryShouldOpen = true;
            }
          }
        });

        if (categoryShouldOpen) {
          autoOpenCategories[category.title] = true;
        }
      });

      // Update open categories state
      if (Object.keys(autoOpenCategories).length > 0) {
        setOpenMainCategories((prev) => ({ ...prev, ...autoOpenCategories }));
      }

      // Update open sections state
      if (Object.keys(autoOpenSections).length > 0) {
        setOpenSubcategories((prev) => ({ ...prev, ...autoOpenSections }));
      }

      // Handle auto-select options based on "selected": true
      const autoSelectedFilters = {};

      // Skip auto-selection if filters were just cleared
      if (!justClearedRef.current) {
        newFilters.forEach((category) => {
          category.sections.forEach((section) => {
            section.options.forEach((option) => {
              if (
                option.selected === true &&
                suppressAllAutoSelectRef.current !== true &&
                !suppressedAutoSelectedRef.current.has(
                  `${buildSectionKey(category.title, section.label)}::${getOptionKey(option)}`
                )
              ) {
                const categoryKey = buildSectionKey(
                  category.title,
                  section.label
                );
                if (!autoSelectedFilters[categoryKey]) {
                  autoSelectedFilters[categoryKey] = [];
                }
                autoSelectedFilters[categoryKey].push(normalizeOption(option));
              }
            });
          });
        });
      } else {
        // Reset the justCleared flag after skipping auto-selection
        justClearedRef.current = false;
      }

      // Update filters with auto-selected options
      if (Object.keys(autoSelectedFilters).length > 0) {
        // Avoid triggering an immediate extra fetch due to selectedFilters change
        skipNextFetchRef.current = true;
        setFilters((prevFilters) => {
          const updatedFilters = { ...prevFilters };
          Object.entries(autoSelectedFilters).forEach(([category, options]) => {
            const existingOptions = updatedFilters[category] || [];
            const newOptions = options.filter(
              (newOpt) =>
                !existingOptions.some(
                  (existingOpt) =>
                    getOptionKey(existingOpt) === getOptionKey(newOpt)
                )
            );
            updatedFilters[category] = [...existingOptions, ...newOptions];
          });
          return updatedFilters;
        });
      }

      // ✅ Stop pagination if fewer than `limit` products returned
      if (newProducts.length < limit) {
        setHasMore(false);
      }

      if (newProducts.length === 0) {
        setHasMore(false);
      } else {
        setProductData((prev) => [...prev, ...newProducts]);
      }
    } catch (err) {
      // Don't log error if request was cancelled
      if (err.name !== "CanceledError" && err.code !== "ERR_CANCELED") {
        console.error(err);
      }
    } finally {
      setLoading(false);
      setLoader(false);
      setIsSkeletonLoading(false);
      // Clear reset flag once first page loads successfully
      if (page === 1) {
        isResettingPaginationRef.current = false;
      }
    }
  }, [
    APIURL,
    categoryId,
    page,
    sortOption,
    hasMore,
    loading,
    selectedFilters,
    isPendingActive,
    sortReady,
  ]);

  // Fetch products when page, sort, or filters change
  useEffect(() => {
    const initFetchProduct = async () => {
      if (skipNextFetchRef.current) {
        // Clear the flag and skip this effect run
        skipNextFetchRef.current = false;
        return;
      }
      await fetchProducts();
    }
    initFetchProduct()
  }, [page, sortOption, selectedFilters, isPendingActive, sortReady]);

  const fetchDefaultSorting = (sorting) => {
    switch (sorting) {
      case 'use_store_settings':
        return '0'
      case 'newest':
        return '3'
      case 'best_selling':
        return '4'
      case 'price_asc':
        return '1'
      case 'price_desc':
        return '2'
      default:
        return '0'
    }

  }

  // IntersectionObserver to trigger next page
  useEffect(() => {
    if (loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMore &&
          !loading &&
          !isPendingActive &&
          !isResettingPaginationRef.current &&
          productData.length > 0 && // Don't trigger if no products loaded yet
          (page > 1 || productData.length >= limit) // Ensure first page is fully loaded
        ) {
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 1.0 }
    );

    if (observerRef.current) observer.observe(observerRef.current);

    return () => {
      if (observerRef.current) observer.unobserve(observerRef.current);
    };
  }, [loading, hasMore, isPendingActive, productData.length, page]);

  // Control body overflow based on loader state
  useEffect(() => {
    handleBodyOverflow(loader);
  }, [loader]);

  // Cleanup abort controller and debounce timer on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      // Restore body overflow on unmount
      handleBodyOverflow(false);
    };
  }, []);

  return (
    <FilterContext.Provider
      value={{
        filters,
        pendingFilters,
        pendingFiltersList,
        openMainCategories,
        openSubcategories,
        selectedFilters,
        handleFilterChange,
        applyPendingFilters,
        clearPendingFilters,
        removePendingFilter,
        seedPendingFromFilters,
        beginPendingSession,
        endPendingSession,
        isPendingActive,
        toggleMainCategory,
        toggleSubcategory,
        removeFilter,
        clearAllFilters,
        sortOption,
        handleSortChange,
        APIURL,
        productData,
        observerRef,
        loading,
        hasMore,
        filterData,
        loader,
        isSkeletonLoading,
        isMobile,
        categoryName,
        categoryDescription,
        productCount,
        // pathLink,
        setMobilePopUpData,
        mobilePopUpData,
        openPopup,
        setopenPopup,
        mappingRootRedirection
      }}
    >
      {children}
    </FilterContext.Provider>
  );
};